import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { format_date } from '@/utils/date_formatter';
import BatchUpload from '@/components/BatchUpload/BatchUpload.vue';
import CustomTable from '@/components/CustomTable/CustomTable.vue';

export default {
  name: 'AssignedWorkflows',
  mixins: [MAIN_CONTROLLER],
  components: {
    BatchUpload,
    CustomTable
  },

  data: () => ({
    tableColumns: [
      { key: 'workflow_name', label: 'Workflow' },
      { key: 'application_status', label: 'Status' },
      { key: 'date_created', label: 'Created' }
    ],
  }),

  methods: {
    /**
     * Fetch applications for CustomTable with pagination
     */
    async fetchApplications(page = 1, search = '') {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const GET_APPLICATIONS_URL = `${BASE_URL}/verify_api/customer_management_api/get_assigned_customer_applications_api/`;

      try {
        const response = await fetch(GET_APPLICATIONS_URL, {
          method: 'GET',
          headers: {
            'Authorization': `Token ${TOKEN}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        
        if (!Array.isArray(data.customer_applications)) {
          return { results: [], count: 0 };
        }

        // Flatten workflow data into individual application rows
        const flattenedApplications = [];
        data.customer_applications.forEach(workflow => {
          workflow.applications.forEach(app => {
            flattenedApplications.push({
              ...app,
              workflow_id: workflow.workflow_id,
              workflow_name: workflow.workflow_name,
              workflow_description: workflow.workflow_description,
              company_name: workflow.company_name,
              allow_batch_onboarding: workflow.allow_batch_onboarding
            });
          });
        });

        // Filter by search if provided
        let filtered = flattenedApplications;
        if (search) {
          const searchLower = search.toLowerCase();
          filtered = flattenedApplications.filter(app => 
            app.workflow_name?.toLowerCase().includes(searchLower) ||
            app.company_name?.toLowerCase().includes(searchLower) ||
            app.application_status?.toLowerCase().includes(searchLower) ||
            app.on_behalf_info?.full_name?.toLowerCase().includes(searchLower)
          );
        }

        // Simple client-side pagination (server doesn't support it)
        const perPage = 10;
        const start = (page - 1) * perPage;
        const paginatedResults = filtered.slice(start, start + perPage);

        return {
          results: paginatedResults,
          count: filtered.length
        };
      } catch (error) {
        console.error('Error fetching assigned workflows:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load your applications. Please try again.',
          confirmButtonText: 'OK'
        });
        return { results: [], count: 0 };
      }
    },

    /**
     * Continue with a specific application
     */
    continueApplication(application_id, workflow_id) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const CONTINUE_URL = `${BASE_URL}/verify_api/customer_management_api/continue_customer_application_api/`;

      // Show loading
      Swal.fire({
        title: 'Loading...',
        text: 'Preparing your application',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      $.ajax({
        method: "POST",
        url: CONTINUE_URL,
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        data: JSON.stringify({
          application_id: application_id
        }),
        success: (response) => {
          if (response.status === 'success') {
            // Store the workflow ID in the store
            this.$store.dispatch('update_workflow_id', workflow_id);
            
            // Store the application ID for later use
            sessionStorage.setItem('current_application_id', application_id);
            
            // Check terms and conditions
            this.check_terms_and_conditions(workflow_id);
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to continue application',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error continuing application:', error);
          const errorMessage = error.responseJSON?.message || 'An error occurred while continuing the application';
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: errorMessage,
            confirmButtonText: 'OK'
          });
        }
      });
    },

    /**
     * Check terms and conditions before proceeding
     */
    check_terms_and_conditions(workflow_id) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const CHECK_TERMS_URL = `${BASE_URL}/verify_api/customer_management_api/check_terms_and_conditions_confirmation/${workflow_id}`;

      $.ajax({
        method: "GET",
        url: CHECK_TERMS_URL,
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        success: (response) => {
          if (response.status === 'Success' && response.is_confirmed === true) {
            Swal.close();
            this.fetch_current_service();
          } else if (response.status === 'error' && response.is_confirmed === false) {
            Swal.close();
            // Redirect to terms and conditions page
            this.$router.push({
              name: 'customer_consent',
              query: { workflow_id: workflow_id }
            });
          }
        },
        error: (error) => {
          console.error('Error checking terms and conditions:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error while confirming terms and conditions',
            confirmButtonText: 'OK'
          });
        }
      });
    },

    /**
     * Format date to readable string
     */
    formatDate(date) {
      return format_date(date);
    },

    /**
     * Format date to relative time (e.g., "2 days ago")
     */
    formatRelativeDate(date) {
      const now = new Date();
      const past = new Date(date);
      const diffTime = Math.abs(now - past);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return 'today';
      } else if (diffDays === 1) {
        return 'yesterday';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      } else if (diffDays < 365) {
        const months = Math.floor(diffDays / 30);
        return `${months} month${months > 1 ? 's' : ''} ago`;
      } else {
        const years = Math.floor(diffDays / 365);
        return `${years} year${years > 1 ? 's' : ''} ago`;
      }
    },

    /**
     * Truncate text to specified length
     */
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    /**
     * Get CSS class for application status
     */
    getApplicationStatusClass(status) {
      const statusMap = {
        'In Progress': 'status-in-progress',
        'Assigned': 'status-assigned',
        'Completed': 'status-completed'
      };
      return statusMap[status] || '';
    },

    /**
     * Get badge class for status
     */
    getStatusBadgeClass(status) {
      const badgeMap = {
        'In Progress': 'bg-primary',
        'Assigned': 'bg-warning text-dark',
        'Completed': 'bg-success'
      };
      return badgeMap[status] || 'bg-secondary';
    },

    /**
     * Open batch upload modal
     */
    open_batch_upload_modal(workflow_id, workflow_name) {
      this.$refs.batchUpload.open_batch_upload_modal(workflow_id, workflow_name);
    },

    /**
     * Handle batch upload completion
     */
    handle_batch_complete(result) {
      // Show notification
      Swal.fire({
        icon: result.success ? 'success' : 'warning',
        title: 'Batch Processing Complete',
        html: `
          <div class="text-start">
            <p>Successfully processed <strong>${result.successful_count || 0}</strong> ${result.type || 'items'}</p>
            ${result.failed_count > 0 ? `<p class="text-danger">${result.failed_count} failed</p>` : ''}
          </div>
        `,
        confirmButtonText: 'OK'
      });
    }
  },

  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
  }
}
